/**
 * A DESPESCA — o único dia em que se sabe o que estava dentro da represa.
 *
 * Durante o ciclo inteiro o app estima: quantos peixes, quanto pesam, quanta
 * ração cabe. Nada disso é medido. Aqui, e só aqui, o lote inteiro passa pela
 * balança e pela contagem, e o número que sai é o VEREDITO — é ele que diz o
 * que as estimativas dos últimos oito meses valiam.
 *
 * ── A FORMA: cabeçalho, rodadas, fechamento ───────────────────────────────
 *
 *   abre a ficha  →  cestão, cestão, cestão…  →  fecha dizendo TOTAL ou PARCIAL
 *
 * É a mesma forma da transferência e da repicagem. O que muda é o que cada
 * rodada carrega e para onde o peixe vai.
 *
 * ── UMA PESSOA SÓ REGISTRA ────────────────────────────────────────────────
 *
 * Decisão do gestor, literal: «NUNCA [duas]; sempre será um operador na
 * despesca registrando e o outro no munck tirando os peixes; um anota, o outro
 * retira o cestão». Isso simplifica a tela inteira — não há disputa entre
 * aparelhos, não há mesclagem, não há duas verdades para conciliar.
 *
 * ── A TARA NÃO APARECE ────────────────────────────────────────────────────
 *
 * A balança grande tem botão de tara e já entrega o líquido. Campo que não
 * serve rouba segundos de quem está em pé no sol com o munck esperando.
 *
 * ── O RASCUNHO É MAIS IMPORTANTE AQUI QUE EM QUALQUER TELA ────────────────
 *
 * Uma despesca são quarenta cestões e algumas horas. Perder isso por causa de
 * uma tela que bloqueou não faz a pessoa digitar de novo — faz ela voltar para
 * o papel, e com razão.
 *
 * ── O QUE VAI PARA O BANCO É O PESADO, NÃO O COBRADO ──────────────────────
 *
 * O desconto de umidade é acerto comercial; o peixe saiu da represa inteiro.
 * O romaneio mostra os dois, e só o de cima atravessa para o histórico.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  listarRonda, parametrosDoCalculo, registrarDespesca,
  type PontoDaRonda, type ParametrosDoCalculo, type Perfil,
} from '../acesso/api'
import { apurar, blocoVazio, BlocosDePesagem, type Bloco } from './BlocosDePesagem'
import { montarRomaneio, precoInvalido, type Romaneio } from '../dominio/romaneio'
import { RomaneioPapel, type CabecalhoDoRomaneio } from './RomaneioPapel'
import { ExportarRomaneio, RomaneioAberto, RomaneioNaTela } from './RomaneioAberto'
import { diaCurto, inteiro, numero, numeroNaFaixa, soNumero } from '../dominio/unidades'
import { hojeISO, SeletorDeDia } from './SeletorDeDia'
import { pendentes } from '../acesso/fila'

const RASCUNHO = 'cunhatai.despesca'

/**
 * O ÚLTIMO ROMANEIO GRAVADO NESTE APARELHO — para abrir logo depois de gravar.
 *
 * A tela se refaz ao gravar (o formulário zera), e o romaneio ia junto. Fica
 * guardado aqui até alguém dispensar, e funciona sem sinal: a despesca pode
 * estar ainda na fila. Os anteriores ficam em Menu › Romaneios.
 */
const ULTIMO_ROMANEIO = 'cunhatai.ultimo-romaneio'

interface UltimoRomaneio {
  romaneio: Romaneio
  cabecalho: CabecalhoDoRomaneio
  /** O registro na fila — para dizer se ainda está só no aparelho. */
  id: string
  /** O dia em que foi gravado: a faixa sai sozinha quando o dia vira. */
  gravadoEm: string
}

function lerUltimo(): UltimoRomaneio | null {
  try {
    const bruto = localStorage.getItem(ULTIMO_ROMANEIO)
    const u = bruto ? (JSON.parse(bruto) as UltimoRomaneio) : null
    if (!u || !Array.isArray(u.romaneio?.linhas) || typeof u.cabecalho?.dia !== 'string') return null
    // AVISO QUE NUNCA MUDA ENSINA A NÃO LER. A faixa é do dia da gravação; no
    // dia seguinte ela some, e o romaneio continua em Menu › Romaneios.
    if (u.gravadoEm !== hojeISO()) {
      localStorage.removeItem(ULTIMO_ROMANEIO)
      return null
    }
    return u
  } catch {
    return null
  }
}

function guardarUltimo(u: UltimoRomaneio | null): void {
  try {
    if (u) localStorage.setItem(ULTIMO_ROMANEIO, JSON.stringify(u))
    else localStorage.removeItem(ULTIMO_ROMANEIO)
  } catch {
    /* armazenamento bloqueado: o romaneio continua em Menu › Romaneios */
  }
}

/** Quando o parâmetro ainda não existe no banco — ou a fazenda é outra. */
const ESPECIES_PADRAO = ['tambaqui', 'tambatinga', 'pirapitinga', 'pacu', 'outra']

interface Rascunho {
  cicloId: string
  dia: string
  comprador: string
  destino: string
  blocos: Bloco[]
  desconto: string
  precos: Record<string, string>
  encerra: boolean
  remanescente: string
  observacao: string
}

function lerRascunho(): Rascunho | null {
  try {
    const bruto = localStorage.getItem(RASCUNHO)
    const r = bruto ? (JSON.parse(bruto) as Rascunho) : null
    return r && Array.isArray(r.blocos) && r.blocos.length > 0 ? r : null
  } catch {
    return null
  }
}

function guardarRascunho(r: Rascunho | null): void {
  try {
    if (r) localStorage.setItem(RASCUNHO, JSON.stringify(r))
    else localStorage.removeItem(RASCUNHO)
  } catch {
    /* armazenamento bloqueado: a ficha vale só enquanto a tela estiver aberta */
  }
}

/** A lista da fazenda, de `parametros`. Ponto e vírgula separa — a vírgula é decimal. */
function especiesDe(calculo: ParametrosDoCalculo | null): string[] {
  const achado = calculo?.parametros.find((p) => p.chave === 'despesca.especies')
  const lista = (achado?.valor ?? '')
    .split(';')
    .map((x) => x.trim())
    .filter((x) => x !== '')
  return lista.length > 0 ? lista : ESPECIES_PADRAO
}

export function Despesca({ aoGravar, perfil }: { aoGravar: () => void; perfil: Perfil }) {
  const salvo = useMemo(lerRascunho, [])

  const [pontos, setPontos] = useState<PontoDaRonda[]>([])
  const [calculo, setCalculo] = useState<ParametrosDoCalculo | null>(null)
  const [cicloId, setCicloId] = useState(salvo?.cicloId ?? '')
  const [dia, setDia] = useState(salvo?.dia ?? hojeISO())
  const [comprador, setComprador] = useState(salvo?.comprador ?? '')
  const [destino, setDestino] = useState(salvo?.destino ?? '')
  const [blocos, setBlocos] = useState<Bloco[]>(salvo?.blocos ?? [blocoVazio('', 'tambaqui')])
  const [desconto, setDesconto] = useState(salvo?.desconto ?? '')
  const [precos, setPrecos] = useState<Record<string, string>>(salvo?.precos ?? {})
  const [encerra, setEncerra] = useState(salvo?.encerra ?? true)
  const [remanescente, setRemanescente] = useState(salvo?.remanescente ?? '')
  const [observacao, setObservacao] = useState(salvo?.observacao ?? '')
  const [retomando, setRetomando] = useState(salvo !== null)
  const [confirmando, setConfirmando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [ultimo, setUltimo] = useState<UltimoRomaneio | null>(lerUltimo)

  useEffect(() => {
    Promise.all([listarRonda(dia), parametrosDoCalculo()])
      .then(([r, p]) => {
        setPontos(r)
        setCicloId((atual) => (r.some((x) => x.ciclo_id === atual) ? atual : r[0]?.ciclo_id ?? ''))
        setCalculo(p)
        // O desconto de partida vem do banco, e quem despesca confirma ou muda.
        setDesconto((atual) => {
          if (atual !== '') return atual
          const achado = p.parametros.find((x) => x.chave === 'despesca.desconto_umidade_padrao')
          return achado?.valor ?? ''
        })
        setErro(null)
      })
      .catch((falha: unknown) => {
        setErro(falha instanceof Error ? falha.message : 'não deu para carregar')
      })
      .finally(() => setCarregando(false))
    // A ronda muda com o dia: outro dia, outro saldo estimado na represa.
  }, [dia])

  useEffect(() => {
    const temAlgo = blocos.some((b) => b.quantidade !== '' || b.bruto !== '')
    guardarRascunho(
      temAlgo
        ? { cicloId, dia, comprador, destino, blocos, desconto, precos, encerra, remanescente, observacao }
        : null,
    )
  }, [cicloId, dia, comprador, destino, blocos, desconto, precos, encerra, remanescente, observacao])

  const especies = useMemo(() => especiesDe(calculo), [calculo])
  const ponto = pontos.find((p) => p.ciclo_id === cicloId) ?? null
  const romaneio = montarRomaneio(blocos, false, desconto, precos)
  const cabecalho: CabecalhoDoRomaneio = {
    represa: ponto?.tanque ?? '',
    ciclo: ponto?.ciclo ?? '',
    dia,
    comprador,
    destino,
    observacao: observacao.trim(),
  }
  // O último romaneio só aparece sem despesca em andamento: dois papéis
  // montados ao mesmo tempo sairiam os dois na mesma folha.
  const painelDoUltimo =
    ultimo && romaneio === null ? (
      <PainelDoUltimo
        semValores={perfil !== 'gestor' && perfil !== 'administrador'}
        ultimo={ultimo}
        aoDispensar={() => {
          guardarUltimo(null)
          setUltimo(null)
        }}
      />
    ) : null
  const total = apurar(blocos, false)
  const nRemanescente = numeroNaFaixa(remanescente, 0, 1_000_000)

  if (carregando) return <p className="aviso">Carregando…</p>

  // Sem resposta, o app não sabe quais ciclos existem. Dentro do ramo porque
  // `erro` também guarda falha de gravação.
  // O painel do último romaneio vem junto: despescado o último tanque, a lista
  // de ciclos fica vazia — e é justamente aí que o romaneio é procurado.
  if (pontos.length === 0) {
    return (
      <>
        {painelDoUltimo}
        {erro !== null ? (
          <p className="aviso-erro">Não deu para carregar os ciclos: {erro}</p>
        ) : (
          <p className="aviso">Nenhum ciclo aberto. Não há peixe para despescar.</p>
        )}
      </>
    )
  }

  const pronto =
    ponto !== null &&
    romaneio !== null &&
    total !== null &&
    (!encerra ? nRemanescente !== null || remanescente.trim() === '' : true)

  function gravar() {
    if (!ponto || !romaneio) return
    setErro(null)
    try {
      // GUARDADO ANTES DE GRAVAR, com o que foi emitido de fato — o cabeçalho
      // e o romaneio desta tela, e não uma reconstrução.
      const item = registrarDespesca(
        {
          ciclo_id: ponto.ciclo_id,
          data: dia,
          comprador: comprador.trim() === '' ? null : comprador.trim(),
          destino: destino.trim() === '' ? null : destino.trim(),
          // Porcentagem vira fração: o banco guarda 0,03, não 3.
          desconto_umidade: romaneio.descontoPct / 100,
          encerra,
          remanescente: encerra ? null : nRemanescente,
          saldo_mostrado: ponto.peixes_hoje,
          observacao: observacao.trim() === '' ? null : observacao.trim(),
          ciclo_codigo: ponto.ciclo,
          linhas: romaneio.linhas.map((l) => ({
            especie: l.especie,
            quantidade: l.peixes,
            // O PESADO, nunca o cobrado.
            peso_total_kg: l.pesadoKg,
            preco_kg_brl: l.precoKg,
          })),
        },
        { rotulo: ponto.tanque },
      )
      const emitido: UltimoRomaneio = { romaneio, cabecalho, id: item.id, gravadoEm: hojeISO() }
      guardarUltimo(emitido)
      setUltimo(emitido)
      setBlocos([blocoVazio('', 'tambaqui')])
      setComprador('')
      setDestino('')
      setPrecos({})
      setRemanescente('')
      setObservacao('')
      guardarRascunho(null)
      setRetomando(false)
      setConfirmando(false)
      aoGravar()
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para gravar')
    }
  }

  return (
    <>
    {painelDoUltimo}
    <section className="bloco">
      <h2>Despesca</h2>

      {retomando && (
        <p className="secundario">Retomando uma despesca que ficou aberta neste aparelho.</p>
      )}

      {/* ---- O CABEÇALHO, como a ficha de papel ---------------------------- */}
      <label className="campo">
        <span className="campo-rotulo">De qual represa?</span>
        <select value={cicloId} onChange={(e) => setCicloId(e.target.value)}>
          {pontos.map((p) => (
            <option key={p.ciclo_id} value={p.ciclo_id}>
              {p.tanque} · {p.ciclo}
            </option>
          ))}
        </select>
      </label>

      <SeletorDeDia dia={dia} aoMudar={setDia} />

      <div className="pesagem-campos">
        <label className="campo">
          <span className="campo-rotulo">Comprador</span>
          <input
            value={comprador}
            onChange={(e) => setComprador(e.target.value)}
            placeholder="Frigorífico do Vale"
          />
        </label>
        <label className="campo">
          <span className="campo-rotulo">Destino (opcional)</span>
          <input
            value={destino}
            onChange={(e) => setDestino(e.target.value)}
            placeholder="Ariquemes"
          />
        </label>
      </div>

      {/* ---- AS RODADAS ---------------------------------------------------- */}
      <h3>Os cestões</h3>
      <BlocosDePesagem
        blocos={blocos}
        comTara={false}
        comContador
        especies={especies}
        rotulo="Cestão"
        aoMudar={setBlocos}
      />

      {/* ---- O ROMANEIO ---------------------------------------------------- */}
      {romaneio && (
        <>
          <h3>O romaneio</h3>

          <label className="campo">
            <span className="campo-rotulo">Desconto de umidade (%)</span>
            <input
              value={desconto}
              onChange={(e) => setDesconto(soNumero(e.target.value))}
              inputMode="decimal"
              placeholder="3"
            />
            <span className="campo-dica">
              Vale para a despesca inteira, não por espécie. Em branco é sem desconto.
            </span>
          </label>

          {romaneio.linhas.map((l) => (
            <label className="campo" key={l.especie}>
              <span className="campo-rotulo">Preço do {l.especie} (R$/kg)</span>
              <input
                value={precos[l.especie] ?? ''}
                onChange={(e) =>
                  setPrecos((p) => ({ ...p, [l.especie]: soNumero(e.target.value) }))
                }
                inputMode="decimal"
                placeholder="8,50"
                className={precoInvalido(precos[l.especie] ?? '') ? 'campo-invalido' : undefined}
              />
              {precoInvalido(precos[l.especie] ?? '') && (
                <span className="campo-aviso">em reais e centavos: 11,55</span>
              )}
            </label>
          ))}

          <RomaneioNaTela romaneio={romaneio} />
          <Confronto romaneio={romaneio} ponto={ponto} />
        </>
      )}

      {/* ---- O FECHAMENTO -------------------------------------------------- */}
      <h3>Encerrou o lote?</h3>
      <div className="apuracao">
        <button
          type="button"
          className={encerra ? 'unidade-botao ativo' : 'unidade-botao'}
          onClick={() => setEncerra(true)}
        >
          despesca total
        </button>
        <button
          type="button"
          className={encerra ? 'unidade-botao' : 'unidade-botao ativo'}
          onClick={() => setEncerra(false)}
        >
          parcial, fica peixe
        </button>
      </div>

      {encerra ? (
        <p className="aviso-forte">
          Isto <strong>encerra o ciclo</strong>. O tanque zera e para de mostrar saldo, e o que
          sobrar da conta vira o residual do fechamento — diagnóstico, nunca cobrança.
        </p>
      ) : (
        <label className="campo">
          <span className="campo-rotulo">Quantos peixes ficaram na represa?</span>
          <input
            value={remanescente}
            onChange={(e) => setRemanescente(soNumero(e.target.value))}
            inputMode="numeric"
            placeholder="700"
          />
          <span className="campo-dica">
            A estimativa de quem estava lá. O app continua contando pelo livro-razão; guardar
            as duas é o que mostra, com o tempo, para que lado o olho erra.
          </span>
        </label>
      )}

      <label className="campo">
        <span className="campo-rotulo">Observação (opcional)</span>
        <input
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="rede passou duas vezes; peixe miúdo no fundo"
        />
      </label>

      {erro && <p className="aviso-erro">{erro}</p>}

      {/* Dupla confirmação: a despesca total encerra o ciclo, e encerrar é a
          coisa mais difícil de desfazer que este app faz hoje. */}
      {!confirmando ? (
        <button
          type="button"
          className="confirma"
          disabled={!pronto}
          onClick={() => setConfirmando(true)}
        >
          ✓ registrar despesca
        </button>
      ) : (
        <div className="confirmacao">
          <p>
            {encerra ? (
              <>
                Encerrar o ciclo do <strong>{ponto?.tanque}</strong> com{' '}
                <strong>{inteiro(romaneio?.peixes ?? 0)} peixes</strong> e{' '}
                <strong>{numero(romaneio?.pesadoKg ?? 0, 2)} kg</strong>?
              </>
            ) : (
              <>
                Registrar a saída de <strong>{inteiro(romaneio?.peixes ?? 0)} peixes</strong> e{' '}
                <strong>{numero(romaneio?.pesadoKg ?? 0, 1)} kg</strong> do{' '}
                <strong>{ponto?.tanque}</strong>?
              </>
            )}
          </p>
          <div className="apuracao">
            <button type="button" className="discreto" onClick={() => setConfirmando(false)}>
              voltar
            </button>
            <button type="button" className="confirma" onClick={gravar}>
              sim, registrar
            </button>
          </div>
        </div>
      )}

      {romaneio && (
        <>
          <ExportarRomaneio romaneio={romaneio} cabecalho={cabecalho} />
          {/* O DOCUMENTO, invisível na tela e o único que vai para o papel.
              Fica sempre montado para que «Imprimir» do próprio navegador
              funcione — inclusive o do menu, que não passa pelo botão. */}
          <RomaneioPapel romaneio={romaneio} cabecalho={cabecalho} />
        </>
      )}
    </section>
    </>
  )
}

/**
 * «DESPESCA GRAVADA — ABRIR O ROMANEIO», logo depois de gravar.
 *
 * Fechado de início: o romaneio aberto ocupa a tela inteira, e quem acabou de
 * gravar pode estar só começando a despesca do tanque seguinte.
 */
function PainelDoUltimo({
  ultimo, aoDispensar, semValores,
}: {
  ultimo: UltimoRomaneio
  aoDispensar: () => void
  semValores: boolean
}) {
  const [aberto, setAberto] = useState(false)
  const { cabecalho } = ultimo
  const naFila = pendentes().find((p) => p.id === ultimo.id)
  // O QUE SE AFIRMA É O QUE SE SABE. Na fila, ela está só neste aparelho; se o
  // servidor recusou, é isso que se diz — e não «gravada».
  const situacao = !naFila
    ? 'registrada'
    : naFila.recusa
      ? 'recusada pelo servidor — veja em «ver o que está esperando»'
      : 'guardada neste aparelho — sobe no Sincronizar'
  return (
    <section className="bloco ultimo-romaneio">
      <p>
        <strong>
          Despesca do {cabecalho.represa || 'tanque'} ({diaCurto(cabecalho.dia)}): {situacao}.
        </strong>{' '}
        O romaneio fica guardado em <strong>Menu › Romaneios</strong>.
      </p>
      <div className="apuracao">
        <button type="button" className="discreto" onClick={() => setAberto((a) => !a)}>
          {aberto ? 'fechar o romaneio' : 'abrir o romaneio'}
        </button>
        <button type="button" className="discreto" onClick={aoDispensar}>
          dispensar
        </button>
      </div>
      {aberto && (
        <RomaneioAberto romaneio={ultimo.romaneio} cabecalho={cabecalho} semValores={semValores} />
      )}
    </section>
  )
}

/**
 * Onde a contagem está, em uma linha.
 *
 * A primeira versão anunciava a quebra: «saíram 82% A MENOS do que o app
 * contava», em tarja avermelhada, com três linhas explicando por que a
 * estimativa erra. O gestor cortou, e por duas razões que valem ficar escritas:
 *
 *   AFIRMAR A QUEBRA ANTES DE ENCERRAR É ADIANTAR UM VEREDITO QUE AINDA NÃO
 *   EXISTE. A despesca está correndo; faltam cestões. Toda porcentagem
 *   calculada no meio dela está errada por construção.
 *
 *   E TARJA VERMELHA PUXA ORELHA. Ninguém ali fez nada errado: a estimativa é
 *   otimista por construção, o que o app já sabe e não precisa repetir na cara
 *   de quem está puxando rede no sol.
 *
 * Sobram os dois números, lado a lado, sem adjetivo. Quem está lá sabe ler.
 */
function Confronto({ romaneio, ponto }: { romaneio: Romaneio; ponto: PontoDaRonda | null }) {
  if (!ponto || ponto.peixes_hoje <= 0) return null

  return (
    <p className="secundario">
      Contagem estimada {inteiro(ponto.peixes_hoje)} peixes · saíram{' '}
      {inteiro(romaneio.peixes)} até agora.
    </p>
  )
}
